import React from 'react';
import DataTable from 'react-data-table-component';
import {
  LearnerListItem,
  LearnerListItemCourse,
  LearnerStatus,
} from '../../../../data/models/Learners/Learner';
import TablePagination from '../../../../uiToolkit/TablePagination';
import {listLearnersText} from '../listLearnersText';
import IconButton from '../../../../uiToolkit/Buttons/IconButton';
import {deleteIcon} from '../../../../assets/images';
import './styles.css';
import Tooltip from '../../../../uiToolkit/Tooltip';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';

interface Props {
  learners: LearnerListItem[];
  onDelete: (id: string) => void;
  onDeleteFromCourse: (learnerId: string, courseId: string) => void;
  pages: number;
  onPageChange: (page: number) => void;
  courseFilterSelected?: boolean;
  onActivateLearner: (learnerId: string) => void;
  onReEnroll: (courseId: string, learnerId: string) => void;
  onCopyEmail: (email: string) => void;
  onResendInvitation: (email: string) => void;
  isLoading: boolean;
}

const columns = (
  onDelete: (id: string) => void,
  onActivate: (id: string) => void,
  onCopyEmail: (email: string) => void,
  onResendInvitation: (email: string) => void,
) => [
  {
    name: 'Name',
    selector: (row: LearnerListItem) => row.name,
    cell: (row: LearnerListItem) => (
      <div className="TableNameContainer" data-tag="allowRowEvents">
        {row.avatar ? (
          <img src={row.avatar} alt={'Profile'} data-tag="allowRowEvents" />
        ) : (
          <div className="LearnerAvatarPlaceholder">
            <span>
              {row.name && row.name.length ? row.name[0].toUpperCase() : ''}
            </span>
          </div>
        )}
        <h5 className="TableText" data-tag="allowRowEvents">
          {row.name}
        </h5>
      </div>
    ),
  },
  {
    name: 'Email',
    selector: (row: LearnerListItem) => row.email,
    cell: (row: LearnerListItem) => (
      <div
        style={{maxWidth: '100%', cursor: 'pointer'}}
        onClick={() => onCopyEmail(row.email)}>
        <Tooltip tooltipText={listLearnersText.copyEmail}>
          <div className="DataTableText">{row.email}</div>
        </Tooltip>
      </div>
    ),
  },
  {
    name: 'Status',
    selector: (row: LearnerListItem) => row.status,
    cell: (row: LearnerListItem) => (
      <h5
        className={`TableText ${
          row.status === LearnerStatus.ACTIVE
            ? 'ActiveLearner'
            : row.status === LearnerStatus.INACTIVE
            ? 'InactiveLearner'
            : 'InvitedStatus'
        }`}
        data-tag="allowRowEvents">
        {row.status === LearnerStatus.ACTIVE
          ? listLearnersText.active
          : row.status === LearnerStatus.INACTIVE
          ? listLearnersText.inactive
          : listLearnersText.invited}
      </h5>
    ),
    id: 'learner_status',
  },

  {
    name: 'Actions',
    cell: (row: LearnerListItem) => (
      <div className="Actions">
        {row.status === LearnerStatus.INACTIVE && (
          <div className="ActivateContainer">
            <IconButton
              onClick={() => onActivate(row.learnerId || '')}
              icon="icon-typeAdd-learner"
              tooltip={listLearnersText.activate}
            />
          </div>
        )}
        {row.status === LearnerStatus.ACTIVE && (
          <IconButton
            onClick={() => onDelete(row.learnerId || '')}
            icon={deleteIcon}
            tooltip={listLearnersText.deleteLearner}
          />
        )}
        {row.status === LearnerStatus.INVITED && (
          <IconButton
            onClick={() => onResendInvitation(row.email)}
            icon="icon-mail"
            tooltip={listLearnersText.resendInvitation}
          />
        )}
      </div>
    ),
    id: 'course_actions',
    grow: 2,
  },
];

interface CourseStatsTableProps {
  data: LearnerListItem;
  onDelete: (id: string) => void;
  onReEnroll: (courseId: string, learnerId: string) => void;
  isLoading: boolean;
}

const CourseStatusTable = ({
  data,
  onDelete,
  onReEnroll,
  isLoading,
}: CourseStatsTableProps) => {
  const columns = [
    {
      name: 'Course name',
      selector: (row: LearnerListItemCourse) => row.name,
      cell: (row: LearnerListItemCourse) => (
        <Tooltip tooltipText={row.name}>
          <h5 className={'TableText'} data-tag="allowRowEvents">
            {row.name}
          </h5>
        </Tooltip>
      ),
    },
    {
      name: 'Progress',
      selector: (row: LearnerListItemCourse) => row.progress + '%',
      id: 'course_progress',
    },
    {
      name: 'Status',
      selector: (row: LearnerListItemCourse) => row.status,
      cell: (row: LearnerListItemCourse) => (
        <h5
          className={`TableText ${
            row.status === LearnerStatus.ACTIVE
              ? 'ActiveLearner'
              : 'InactiveLearner'
          }`}
          data-tag="allowRowEvents">
          {row.status === LearnerStatus.ACTIVE
            ? listLearnersText.active
            : listLearnersText.inactive}
        </h5>
      ),
      id: 'course_progress',
    },
    {
      name: 'Completed lessons',
      selector: (row: LearnerListItemCourse) => row.finishedLessons,
      id: 'course_finished_lessons',
    },
    {
      name: 'Actions',
      cell: (row: LearnerListItemCourse) =>
        row.status === LearnerStatus.ACTIVE ? (
          <div>
            <IconButton
              onClick={() => onDelete(row.courseId)}
              icon={deleteIcon}
              tooltip={listLearnersText.removeFromCourse}
            />
          </div>
        ) : (
          <div className="ActivateContainer">
            <IconButton
              onClick={() => onReEnroll(row.courseId, data.learnerId)}
              icon="icon-typeAdd-learner"
              tooltip={
                data.status === LearnerStatus.INACTIVE
                  ? listLearnersText.activateLearnerToActivateEnrollment
                  : listLearnersText.activateEnrollment
              }
              disabled={data.status === LearnerStatus.INACTIVE}
            />
          </div>
        ),
      id: 'course_actions',
    },
  ];

  return (
    <DataTable
      data={data.courses.sort((x: any, y: any) =>
        x.name.toLowerCase() > y.name.toLowerCase() ? 1 : -1,
      )}
      columns={columns}
      className="SubTable"
      noDataComponent={
        isLoading ? (
          <div className="CoursesLoading">
            <LoadingSpinner size={40} />
          </div>
        ) : (
          <p className="NoCoursesText">{listLearnersText.learnerNotEnrolled}</p>
        )
      }
    />
  );
};

const StudentsTable = React.memo(
  ({
    learners,
    onDelete,
    pages,
    onPageChange,
    onDeleteFromCourse,
    courseFilterSelected,
    onActivateLearner,
    onReEnroll,
    onCopyEmail,
    onResendInvitation,
    isLoading,
  }: Props) => {
    return (
      <DataTable
        data={learners}
        columns={columns(
          onDelete,
          onActivateLearner,
          onCopyEmail,
          onResendInvitation,
        )}
        className="Table"
        onChangePage={onPageChange}
        pagination
        paginationServer
        paginationPerPage={10}
        paginationTotalRows={pages * 10}
        paginationComponent={TablePagination}
        expandableRows
        expandableRowsComponent={({data}: any) => (
          <CourseStatusTable
            data={data}
            onDelete={(courseId: string) =>
              onDeleteFromCourse(data.learnerId, courseId)
            }
            onReEnroll={onReEnroll}
            isLoading={isLoading}
          />
        )}
        striped
        noDataComponent={
          isLoading ? (
            <div className="LearnersLoading">
              <LoadingSpinner size={48} />
            </div>
          ) : (
            <p className="NoLearners">
              {courseFilterSelected
                ? listLearnersText.noLearnersInCourse
                : listLearnersText.noLearners}
            </p>
          )
        }
      />
    );
  },
);

export default StudentsTable;
